import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom'; // Import du hook de navigation
import { getAdminResources } from '../api/api'; // On n'a plus besoin des autres fonctions API ici
import styles from './AdminLists.module.css';
import FilterBar from '../components/FilterBar';

const ResourcesPage = () => {
    // Hook pour naviguer vers une autre page
    const navigate = useNavigate(); 

    // États pour la liste (Data & UI)
    const [resources, setResources] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, total_pages: 1 });
    const [loadingList, setLoadingList] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeSearch, setActiveSearch] = useState('');
    
    // États pour les filtres
    const [filters, setFilters] = useState({
        status: '',
        country: '',
        tauxGestion: { min: '', max: '' },
        tjm: { min: '', max: '' },
    });
    
    // Fonction de chargement des données
    const loadResourcesData = async (targetPage, currentSearch, currentFilters) => {
        setLoadingList(true);
        try {
            const response = await getAdminResources(targetPage, 10, currentSearch, currentFilters);
            setResources(response.data.data);
            setPagination(response.data.pagination);
        } catch (err) {
            console.error("Erreur chargement ressources:", err);
        } finally {
            setLoadingList(false);
        }
    };

    // useEffect pour déclencher le chargement (Debounce sur la recherche)
    useEffect(() => {
        const handler = setTimeout(() => {
            loadResourcesData(pagination.page, activeSearch, filters);
        }, 300);

        return () => clearTimeout(handler);
    }, [pagination.page, filters, activeSearch]);

    // --- Gestionnaires d'événements ---

    const handleFilterChange = (name, value) => {
        setPagination(prev => ({ ...prev, page: 1 }));
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleSearch = (e) => {
        if (e) e.preventDefault();
        setPagination(prev => ({ ...prev, page: 1 }));
        setActiveSearch(searchTerm); 
    };

    const handleResetFilters = () => {
        setSearchTerm('');
        setActiveSearch('');
        setFilters({ status: '', country: '' });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    // Nouvelle fonction : Navigation vers la page de détails
    const handleViewDetails = (id) => {
        navigate(`/admin/resources/${id}`); // Redirige vers /resources/123 par exemple
    };

    // --- Helpers d'affichage ---

    const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '-';

    const formatPercent = (val) => {
        if (val === undefined || val === null || val === '') return '-';
        return `${parseFloat(val)}%`;
    };
    
    const getStatusBadge = (state) => {
        const s = (state || '').toLowerCase();
        if (s.includes('actif') || s.includes('mission')) return styles.badgeSuccess;
        if (s.includes('interco') || s.includes('attente')) return styles.badgeWarning;
        if (s.includes('sorti')) return styles.badgeDanger;
        return styles.badgeInfo;
    };

    // Configuration du composant FilterBar
    const filterConfig = [
        {
            type: 'select',
            name: 'status',
            label: 'Statut',
            value: filters.status,
            options: [
                { value: 'mission', label: 'En Mission' },
                { value: 'interco', label: 'Intercontract' },
                { value: 'sorti', label: 'Sorti' },
            ]
        },
        {
            type: 'select',
            name: 'country',
            label: 'Pays',
            value: filters.country,
            options: [
                { value: 'france', label: 'France' },
                { value: 'tunisie', label: 'Tunisie' },
                { value: 'maroc', label: 'Maroc' },
                { value: 'belgique', label: 'Belgique' },
            ]
        },
    ];

    // --- Rendu ---

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1 className={styles.title}>Gestion des Consultants</h1>
                <div className={styles.searchForm}>
                    <input 
                        type="text" 
                        placeholder="Rechercher..." 
                        className={styles.searchInput} 
                        value={searchTerm} 
                        onChange={(e) => setSearchTerm(e.target.value)} 
                        onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
                    />
                    <button onClick={handleSearch} className={styles.searchBtn}>🔍</button>
                </div>
            </div>

            <FilterBar 
                filters={filterConfig}
                onFilterChange={handleFilterChange}
                onReset={handleResetFilters}
            />

            <div className={styles.tableWrapper}>
                {loadingList ? <div className={styles.loading}>Chargement...</div> : (
                    <>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Nom & Prénom</th>
                                    <th>État</th>
                                    <th>Email</th>
                                    <th>Taux de gestion</th>
                                    <th>Pays</th>
                                    <th style={{textAlign:'center'}}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {resources.map(res => (
                                    <tr key={res.resource_id}>
                                        <td className={styles.fwBold}>{res.lastName} {res.firstName}</td>
                                        <td>
                                            <span className={`${styles.badge} ${getStatusBadge(res.stateResource)}`}>
                                                {res.stateResource || '?'}
                                            </span>
                                        </td>
                                        <td>{res.email}</td>
                                        <td>{formatPercent(res.actualGestion)}</td>
                                        <td>{res.country || '-'}</td>
                                        <td style={{textAlign:'center'}}>
                                            {/* C'est ici que ça change : on navigue au lieu d'ouvrir une modal */}
                                            <button 
                                                className={styles.btnDetail} 
                                                onClick={() => handleViewDetails(res.resource_id)}
                                            >
                                                Voir Dossier
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {resources.length === 0 && (
                                    <tr>
                                        <td colSpan="6" style={{textAlign: 'center', padding: '40px'}}>
                                            Aucun consultant trouvé avec ces critères
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                        
                        <div className={styles.pagination}>
                            <button 
                                disabled={pagination.page === 1} 
                                onClick={() => setPagination({...pagination, page: pagination.page - 1})} 
                                className={styles.pageBtn}
                            >
                                Précédent
                            </button>
                            <span className={styles.pageInfo}>Page {pagination.page} / {pagination.total_pages}</span>
                            <button 
                                disabled={pagination.page === pagination.total_pages} 
                                onClick={() => setPagination({...pagination, page: pagination.page + 1})} 
                                className={styles.pageBtn}
                            >
                                Suivant
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ResourcesPage;